import { DeferredDiffViewer } from "./DeferredDiffViewer";
import { SkillFileDiffPreview } from "./SkillFileDiffPreview";
import type { DiffItem } from "./types";
import { determineMediaFileType, MediaFileType } from "./utils";

interface DiffExpandedContentProps {
  changeType: DiffItem["changeType"];
  previewPath: string;
  oldValue: string;
  newValue: string;
  showUnavailableMessage: boolean;
}

export const DiffExpandedContent = ({
  changeType,
  previewPath,
  oldValue,
  newValue,
  showUnavailableMessage,
}: DiffExpandedContentProps) => {
  const mediaFileType = determineMediaFileType({
    showUnavailableMessage,
    previewPath,
    oldValue,
    newValue,
  });

  switch (mediaFileType) {
    case MediaFileType.UNAVAILABLE:
      return (
        <div className="px-4 py-3 text-sm text-gray-500">
          Original content could not be loaded for this file.
        </div>
      );
    case MediaFileType.CSV: {
      const csvDisplayContent = changeType === "deleted" ? oldValue : newValue;
      const csvOriginalContent = changeType === "modified" ? oldValue : undefined;
      return (
        <div className="min-w-0 bg-white">
          <SkillFileDiffPreview
            path={previewPath}
            content={csvDisplayContent}
            originalContent={csvOriginalContent}
          />
        </div>
      );
    }
    case MediaFileType.BINARY:
      return (
        <div className="grid gap-4 bg-white p-4 md:grid-cols-2">
          {changeType !== "added" ? (
            <div className="min-w-0">
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                Original
              </div>
              <SkillFileDiffPreview path={previewPath} content={oldValue} />
            </div>
          ) : null}
          {changeType !== "deleted" ? (
            <div className="min-w-0">
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                Updated
              </div>
              <SkillFileDiffPreview path={previewPath} content={newValue} />
            </div>
          ) : null}
        </div>
      );
    case MediaFileType.TEXT:
      return (
        <div className="max-h-80 overflow-auto bg-white [contain:content]">
          <DeferredDiffViewer oldValue={oldValue} newValue={newValue} />
        </div>
      );
    default: {
      const exhaustive: never = mediaFileType;
      return exhaustive;
    }
  }
};
